import React, { Fragment, useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

import { DynamicFormLayout } from './DynamicForm';
import fetcher from '../services/fetcher';
import WithFormConfig from '../hocs/WithFormConfig';

import 'react-toastify/dist/ReactToastify.css';
import { formatFormFields } from '../services/commonServices';
import { toastError, toastSuccess, toastWarning } from '../utils/ToastConfig';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import AppContext from '../context';

const DEFAULT_ACTIONS = [
	// TODO: borrar cuando la API este actualizada con las actions para cada formulario.
	{
		key: 'edit',
		uri: '/persons/',
		method: 'PUT'
	},
	{
		key: 'read',
		uri: '/persons/',
		method: 'GET'
	},
	{
		key: 'delete',
		uri: '/persons/',
		method: 'DELETE'
	},
	{
		key: 'write',
		uri: '/persons/',
		method: 'POST'
	}
];

const Detail = ({ config }) => {
	const { id } = useParams();
	const [loading, setLoading] = useState(true);
	const [submitLoading, setSubmitLoading] = useState(false);
	const [dataFromApi, setDataFromApi] = useState({});
	const [formErrors, setFormErrors] = useState({});
	const [massiveCreationData, setMassiveCreationData] = useState();
	const [resetCount, setResetCount] = useState(1);
	const [t] = useTranslation('app');
	const {
		config: { globalParams, mainParams }
	} = useContext(AppContext);
	const history = useHistory();

	// Verificar si el config tiene error 403 (sin hacer return temprano para respetar las reglas de hooks)
	const hasConfigError = config && config.error && config.status === 403;

	const { actions = DEFAULT_ACTIONS } = hasConfigError ? {} : config;

	const [read = false] = hasConfigError ? [false] : actions.filter((action) => action.key === 'read');
	const [edit = false] = hasConfigError ? [false] : actions.filter((action) => action.key === 'edit');

	async function fetchDataFormById() {
		if (hasConfigError) {
			setLoading(false);
			return;
		}
		
		setLoading(true);

		if (read) {
			const extraparams = globalParams && typeof globalParams === 'object' ? globalParams : {};
			const dataFetched = await fetcher({ path: `${read.uri}${id}/`.replace(/\/\//g, '/'), urlParams: extraparams });
			dataFetched && setDataFromApi(dataFetched);
		}

		setLoading(false);
	}

	useEffect(() => {
		fetchDataFormById(id);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	async function submitHandler(data) {
		setSubmitLoading(true);
		const newData = formatFormFields(data);

		const hasFile = Object.values(newData).some((val) => val instanceof File);
		let body = newData;
		if (hasFile) {
			body = new FormData();
			Object.entries(newData).forEach(([key, val]) => {
				if (val !== undefined && val !== null) body.append(key, val);
			});
		}

		const saveRecord = await fetcher({
			path: `${edit.uri}${id}/`,
			method: 'PATCH',
			body,
			...(hasFile ? { file: true } : {})
		});

		if (massiveCreationData) {
			let requests = [];
			const write = massiveCreationData.gridConfig.actions.find((item) => item.key === 'write');
			massiveCreationData.gridData.forEach((item) =>
				requests.push(
					fetcher({
						path: write.uri,
						method: 'POST',
						body: {
							...item,
							[massiveCreationData.gridRelatedField]: id
						}
					}).then((res) => res && !('error' in res))
				)
			);
			const valid = await Promise.all(requests).then((allreq) => !allreq.some((item) => item === false));
			if (!valid) toastError(`Ocurrió un error en la creación masiva!`);
		}

		if (saveRecord.error) {
			setFormErrors(saveRecord.error_data);
			toastError(`${saveRecord.error}`);
		} else {
			await fetchDataFormById();
			setFormErrors({});
			toastSuccess(t('save'));
			if (saveRecord?.warning_message) {
				toastWarning(saveRecord.warning_message);
			}
			if (saveRecord?.go_back) {
				history.goBack();
			}
		}
		setSubmitLoading(false);
	}

	const saveMassiveCreationData = (gridData, gridConfig, gridRelatedField) =>
		setMassiveCreationData({ gridData, gridConfig, gridRelatedField });

	const resetAll = () => {
		fetchDataFormById(id);
		setResetCount((prev) => prev + 1);
	};

	// Mostrar error 403 del config
	if (hasConfigError) {
		return (
			<div className="text-center p-5">
				<h3 className="text-danger">{t('error404')}</h3>
			</div>
		);
	}

	if (loading) {
		return <p>{t('fetchingConfig')}</p>;
	}

	return (
		<Fragment>
			{dataFromApi.error && (dataFromApi.status === 400 || dataFromApi.status === 403) ?
				<div className="text-center p-5">
					<h3 className="text-danger">{t('error404')}</h3>
				</div>
				:
				<>
					<DynamicFormLayout
						key={resetCount}
						config={config}
						onSubmit={submitHandler}
						data={dataFromApi}
						formErrors={formErrors}
						resetAll={resetAll}
						saveMassiveCreationData={saveMassiveCreationData}
						loadingSubmit={submitLoading}
					/>
					<Helmet>
						<script src="preventExit.js" type="text/javascript" />
					</Helmet></>
			}
		</Fragment>
	);
};

Detail.propTypes = {
	config: PropTypes.object.isRequired
};

export default WithFormConfig(Detail);
